/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Button from 'react-bootstrap/Button'
import { Field, Form, Formik } from 'formik'
import { skipToken } from '@reduxjs/toolkit/query/react'
import * as Yup from 'yup'

import Toast from 'components/atoms/Toast'
import CatalogItemImage from 'components/atoms/CatalogItemImage'
import { confirm } from 'components/molecules/Confirmation'
import {
  useCreateCatalogItemMutation,
  useDeleteCatalogItemMutation,
  useGetCatalogItemQuery,
  useSetCatalogItemPictureMutation,
  useUpdateCatalogItemMutation,
} from 'store/services/catalog'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from 'components/lab/user/permissions'
import DashboardContent from '../atoms/DashboardContent'
import routes from '../constants/routes'

const ACCEPTED_TYPES = ['image/png', 'image/jpeg']
const MAX_PICTURE_SIZE = 5 * 1024 * 1024

const validation = Yup.object().shape({
  name: Yup.string().trim().required('Required'),
  sku: Yup.string().trim().required('Required'),
  description: Yup.string().nullable(),
  purchaseUom: Yup.string().nullable(),
  quantityPerPurchaseUom: Yup.number()
    .transform((value, original) => (original === '' || original === null ? null : value))
    .nullable()
    .min(0, 'Must be zero or greater'),
})

interface FormValues {
  name: string
  sku: string
  description: string
  purchaseUom: string
  quantityPerPurchaseUom: string
}

const initialValues: FormValues = {
  name: '',
  sku: '',
  description: '',
  purchaseUom: '',
  quantityPerPurchaseUom: '',
}

const CatalogItemUpsert = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const canEdit = useLabPermission(UserPermission.EDIT_CATALOG)

  const [createCatalogItem] = useCreateCatalogItemMutation()
  const [updateCatalogItem] = useUpdateCatalogItemMutation()
  const [setCatalogItemPicture] = useSetCatalogItemPictureMutation()
  const [deleteCatalogItem] = useDeleteCatalogItemMutation()

  const { data: item } = useGetCatalogItemQuery(id ? { id } : skipToken)

  const handleDelete = () => {
    if (!id) return
    confirm(`catalog item "${item?.name ?? ''}"`)
      .then(async () => {
        try {
          await deleteCatalogItem({ id }).unwrap()
          Toast({ type: 'success', label: 'Catalog item removed!' })
          navigate(`${routes.index}${routes.catalog.root}`)
        } catch (err: any) {
          Toast({ type: 'error', label: err?.data?.message || 'Could not remove catalog item.' })
        }
      })
      .catch(() => {})
  }

  const [pictureFile, setPictureFile] = useState<File | null>(null)
  const [picturePreview, setPicturePreview] = useState<string | null>(null)

  useEffect(() => {
    if (!pictureFile) {
      setPicturePreview(null)
      return undefined
    }
    const url = URL.createObjectURL(pictureFile)
    setPicturePreview(url)
    return () => URL.revokeObjectURL(url)
  }, [pictureFile])

  const init = useMemo<FormValues | null>(() => {
    if (!item) return null
    return {
      name: item.name ?? '',
      sku: item.sku ?? '',
      description: item.description ?? '',
      purchaseUom: item.purchaseUom ?? '',
      quantityPerPurchaseUom:
        item.quantityPerPurchaseUom === null || typeof item.quantityPerPurchaseUom === 'undefined'
          ? ''
          : String(item.quantityPerPurchaseUom),
    }
  }, [item])

  const onPictureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      setPictureFile(null)
      return
    }
    if (!ACCEPTED_TYPES.includes(file.type)) {
      Toast({ type: 'error', label: 'Picture must be a PNG or JPEG image.' })
      return
    }
    if (file.size > MAX_PICTURE_SIZE) {
      Toast({ type: 'error', label: 'Picture must be 5 MB or smaller.' })
      return
    }
    setPictureFile(file)
  }

  return (
    <DashboardContent
      icon="fas fa-boxes"
      title={id ? 'Edit Catalog Item' : 'Create Catalog Item'}
      subtitle={
        <div>
          <Link to={`${routes.index}${routes.catalog.root}`}>{`< Back to Catalog Management`}</Link>
        </div>
      }
      breadcrumb={[
        { label: 'Catalog Management', to: `${routes.index}${routes.catalog.root}` },
        { label: id ? 'Edit' : 'New' },
      ]}
      content={
        <div className="card-body row">
          <div className="col-md-12 col-lg-8 mb-3">
            <div className="card card-primary p-2">
              <div
                className="card-header"
                css={{ color: 'black !important', backgroundColor: 'transparent !important' }}>
                <h3 className="card-title">CATALOG ITEM INFORMATION</h3>
              </div>
              <Formik
                enableReinitialize={true}
                validationSchema={validation}
                initialValues={init ?? initialValues}
                onSubmit={async (values) => {
                  const payload = {
                    name: values.name.trim(),
                    sku: values.sku.trim(),
                    description: values.description || null,
                    purchaseUom: values.purchaseUom || null,
                    quantityPerPurchaseUom:
                      values.quantityPerPurchaseUom === '' ? null : Number(values.quantityPerPurchaseUom),
                  }

                  try {
                    if (id) {
                      await updateCatalogItem({ id, ...payload }).unwrap()
                      if (pictureFile) {
                        await setCatalogItemPicture({ id, picture: pictureFile }).unwrap()
                        setPictureFile(null)
                      }
                      Toast({ type: 'success', label: 'Catalog item updated!' })
                    } else {
                      const created = await createCatalogItem(payload).unwrap()
                      navigate(`${routes.index}${routes.catalog.root}${routes.upsert}${created.id}`)
                      if (pictureFile) {
                        try {
                          await setCatalogItemPicture({ id: created.id, picture: pictureFile }).unwrap()
                          setPictureFile(null)
                        } catch {
                          Toast({ type: 'error', label: 'Item created, but picture upload failed. Please try uploading again.' })
                          return
                        }
                      }
                      Toast({ type: 'success', label: 'Catalog item created!' })
                    }
                  } catch (err: any) {
                    Toast({
                      type: 'error',
                      label: err?.data?.message || 'Could not save catalog item.',
                    })
                  }
                }}>
                {({ values, errors, touched, handleChange }) => (
                  <Form id="catalogItemUpsert" autoComplete="off">
                    <div className="container bg-white mb-3 px-1 p-0">
                      <div className="container pt-3 pb-1">
                        <div className="row">
                          <div className="col-8 form-group textGray">
                            <label htmlFor="name">
                              Name<span className="text-danger">*</span>
                            </label>
                            <Field
                              type="text"
                              name="name"
                              className="form-control"
                              placeholder="Item name"
                              value={values.name}
                              onChange={handleChange}
                              disabled={!canEdit}
                            />
                            {touched.name && errors.name && (
                              <div className="text-danger">{errors.name}</div>
                            )}
                          </div>
                          <div className="col-4 form-group textGray">
                            <label htmlFor="sku">
                              SKU<span className="text-danger">*</span>
                            </label>
                            <Field
                              type="text"
                              name="sku"
                              className="form-control"
                              placeholder="SKU"
                              value={values.sku}
                              onChange={handleChange}
                              disabled={!canEdit}
                            />
                            {touched.sku && errors.sku && (
                              <div className="text-danger">{errors.sku}</div>
                            )}
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-12 form-group textGray">
                            <label htmlFor="description">Description</label>
                            <Field
                              as="textarea"
                              rows={3}
                              name="description"
                              className="form-control"
                              placeholder="Item description"
                              value={values.description}
                              onChange={handleChange}
                              disabled={!canEdit}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-6 form-group textGray">
                            <label htmlFor="purchaseUom">Purchase UOM</label>
                            <Field
                              type="text"
                              name="purchaseUom"
                              className="form-control"
                              placeholder="e.g. Each, Box, Case"
                              value={values.purchaseUom}
                              onChange={handleChange}
                              disabled={!canEdit}
                            />
                          </div>
                          <div className="col-6 form-group textGray">
                            <label htmlFor="quantityPerPurchaseUom">Quantity per Purchase UOM</label>
                            <Field
                              type="number"
                              min="0"
                              step="any"
                              name="quantityPerPurchaseUom"
                              className="form-control"
                              placeholder="e.g. 12"
                              value={values.quantityPerPurchaseUom}
                              onChange={handleChange}
                              disabled={!canEdit}
                            />
                            {touched.quantityPerPurchaseUom && errors.quantityPerPurchaseUom && (
                              <div className="text-danger">{errors.quantityPerPurchaseUom}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      {canEdit && (
                        <div className="col-sm-12 pt-3 pr-0 border-top">
                          <div className="col-sm-12 d-flex justify-content-between">
                            {id ? (
                              <Button variant="danger" type="button" onClick={handleDelete}>
                                Delete Item
                              </Button>
                            ) : (
                              <span />
                            )}
                            <Button variant="primary" type="submit">
                              Save Changes
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>

          <div className="col-md-12 col-lg-4 mb-3">
            <div className="card card-primary p-2">
              <div
                className="card-header"
                css={{ color: 'black !important', backgroundColor: 'transparent !important' }}>
                <h3 className="card-title">PICTURE</h3>
              </div>
              <div className="card-body text-center">
                {picturePreview ? (
                  <img
                    src={picturePreview}
                    alt="Selected catalog item"
                    css={{ width: '100%', height: 220, objectFit: 'contain', borderRadius: 4 }}
                  />
                ) : id && item ? (
                  <CatalogItemImage
                    id={item.id}
                    by="lab"
                    hasPicture={Boolean(item.pictureKey)}
                    height={220}
                  />
                ) : (
                  <div
                    css={{
                      height: 220,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f4f6f9',
                      color: '#adb5bd',
                      borderRadius: 4,
                    }}>
                    <i className="fas fa-image fa-2x" />
                  </div>
                )}

                {canEdit && (
                  <div className="mt-3">
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      className="form-control-file"
                      onChange={onPictureChange}
                    />
                    <small className="form-text text-muted">
                      PNG or JPEG, up to 5 MB.
                      {!id && ' The picture is uploaded when you save the item.'}
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      }
    />
  )
}

export default CatalogItemUpsert
